import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaCheck,
  FaEye,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaUserClock,
} from 'react-icons/fa';
import { userService } from '../../../../services/apiService';
import { getProfileDisplayEmail } from '../../../../utils/displayName';
import PartnerRegistrationDetail from '../components/PartnerRegistrationDetail';
import GarageClusterVerificationPanel from '../components/GarageClusterVerificationPanel';
import {
  getPartnerAccountTypeLabel,
  getPartnerDisplayName,
  isPendingPartnerApplication,
} from '../utils/partnerRegistrationAdmin';

const PENDING_ROLES = new Set(['used_pending', 'vendor', 'garage']);

export default function PartnerApplicationsPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      const raw = response?.data ?? response?.users ?? response;
      const list = Array.isArray(raw) ? raw : (raw?.users ?? raw?.data ?? []);
      setUsers(list.filter((u) => PENDING_ROLES.has((u.role || '').toLowerCase())));
    } catch (err) {
      toast.error(err.message || 'Failed to load applications');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const pending = isPendingPartnerApplication(u);
      if (statusFilter === 'pending' && !pending) return false;
      if (statusFilter === 'verified' && (u.kycStatus || 'Pending') !== 'Verified') return false;
      if (!q) return true;
      const hay = [
        getPartnerDisplayName(u),
        getProfileDisplayEmail(u),
        u.phone,
        getPartnerAccountTypeLabel(u),
        u.role,
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [users, search, statusFilter]);

  const openDetail = async (u) => {
    setSelected(u);
    setDetailUser(null);
    setDetailLoading(true);
    try {
      const id = u._id || u.id;
      const res = await userService.getUserById(id);
      setDetailUser(res?.data ?? res ?? u);
    } catch {
      setDetailUser(u);
    } finally {
      setDetailLoading(false);
    }
  };

  const approve = async (u) => {
    const id = u._id || u.id;
    const role = (u.role || '').toLowerCase();
    const nextRole = role === 'used_pending' ? 'used_vendor' : role === 'garage' ? 'garage' : 'vendor';
    if (!window.confirm(`Approve this application and activate as ${nextRole}?`)) return;
    try {
      setActingId(id);
      await userService.updateUser(id, {
        kycStatus: 'Verified',
        role: nextRole,
        onboardingComplete: true,
      });
      toast.success('Application approved');
      setSelected(null);
      setDetailUser(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Approval failed');
    } finally {
      setActingId(null);
    }
  };

  const reject = async (u) => {
    const id = u._id || u.id;
    if (!window.confirm('Reject this application?')) return;
    try {
      setActingId(id);
      await userService.updateUser(id, { kycStatus: 'Rejected' });
      toast.success('Application rejected');
      setSelected(null);
      setDetailUser(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Reject failed');
    } finally {
      setActingId(null);
    }
  };

  if (selected) {
    const u = detailUser || selected;
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => { setSelected(null); setDetailUser(null); }}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <FaArrowLeft /> Back to list
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={actingId === (u._id || u.id)}
              onClick={() => approve(u)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              {actingId === (u._id || u.id) ? <FaSpinner className="animate-spin" /> : <FaCheck />}
              Approve
            </button>
            <button
              type="button"
              disabled={actingId === (u._id || u.id)}
              onClick={() => reject(u)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
            >
              <FaTimes /> Reject
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/60">
            <h2 className="text-lg font-bold text-gray-900">{getPartnerDisplayName(u)}</h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 uppercase">Email</p>
                <p className="font-medium text-gray-900">{getProfileDisplayEmail(u) || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Phone</p>
                <p className="font-medium text-gray-900">{(u.phoneCode || '+91') + ' ' + (u.phone || '—')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Account type</p>
                <p className="font-medium text-gray-900">{getPartnerAccountTypeLabel(u)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <p className="font-medium text-amber-700">{u.kycStatus || 'Pending'}</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {detailLoading ? (
              <div className="flex justify-center py-16">
                <FaSpinner className="animate-spin text-sky-600 text-3xl" />
              </div>
            ) : (
              <>
                <PartnerRegistrationDetail user={u} />
                {(u.role || '').toLowerCase() === 'garage' && (
                  <div className="mt-6">
                    <GarageClusterVerificationPanel
                      garageId={u._id || u.id}
                      garageDetails={u.garageDetails || {}}
                      onUpdated={(updated) => {
                        const user = updated?.garage || updated;
                        const gd = user?.garageDetails;
                        if (!gd) return;
                        setDetailUser((prev) => ({ ...(prev || u), garageDetails: gd }));
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FaUserClock className="text-sky-600" />
          Partner applications
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          All signup details — account type, business info, address, bank, and documents.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, type..."
            className="dashboard-input w-full pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="dashboard-input min-w-[160px]"
        >
          <option value="pending">Pending only</option>
          <option value="verified">Approved</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <FaSpinner className="animate-spin text-sky-600 text-3xl" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 text-gray-500">
          No partner applications found.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Business</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Account type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((u) => {
                const id = u._id || u.id;
                return (
                  <tr key={id} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3 font-medium text-gray-900">{getPartnerDisplayName(u)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <div>{getProfileDisplayEmail(u) || '—'}</div>
                      <div className="text-xs text-gray-500">{u.phone || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{getPartnerAccountTypeLabel(u)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        (u.kycStatus || 'Pending') === 'Verified'
                          ? 'bg-emerald-50 text-emerald-700'
                          : (u.kycStatus || '') === 'Rejected'
                            ? 'bg-red-50 text-red-700'
                            : 'bg-amber-50 text-amber-700'
                      }`}>
                        {u.kycStatus || 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openDetail(u)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sky-700 hover:bg-sky-50 text-sm font-medium"
                      >
                        <FaEye /> View all
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
